import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Copy, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { ACTIVATION_MESSAGE, ACTIVATION_URL } from "@/lib/activationMessage";

export default function Autorizados() {
    const { user } = useAuth();
    const ue = user?.usuario_edificio;
    const unidadId = ue?.unidad_id;

    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ nombre: "", apellido: "", email: "", whatsapp: "" });
    const [delId, setDelId] = useState(null);
    const [busy, setBusy] = useState(false);

    const linkActivacion = ACTIVATION_URL;

    const load = () =>
        api.get("/usuarios-edificio", { params: { unidad_id: unidadId } })
            .then((r) => setItems(r.data.filter((u) => !u.es_master)));

    useEffect(() => { if (unidadId) load(); /* eslint-disable-next-line */ }, []);

    const submit = async () => {
        if (!form.nombre.trim() || !form.apellido.trim() || !form.email.trim()) {
            return toast.error("Completá nombre, apellido y email");
        }
        try {
            await api.post("/usuarios-edificio/pendiente", {
                nombre: form.nombre.trim(),
                apellido: form.apellido.trim(),
                email: form.email.trim().toLowerCase(),
                whatsapp: form.whatsapp.trim().replace(/\s+/g, " "),
            });
            toast.success("Autorizado cargado");
            setForm({ nombre: "", apellido: "", email: "", whatsapp: "" });
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async () => {
        if (!delId) return;
        setBusy(true);
        try {
            await api.delete(`/usuarios-edificio/mi-unidad/${delId}`);
            setItems((arr) => arr.filter((x) => x.usuario_edif_id !== delId));
            setDelId(null);
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setBusy(false);
        }
    };

    const copiarLink = async () => {
        try {
            await navigator.clipboard.writeText(ACTIVATION_MESSAGE);
            toast.success("Mensaje copiado");
        } catch {
            toast.error("No se pudo copiar. Copialo manualmente.");
        }
    };

    return (
        <div className="max-w-2xl">
            <PageHeader
                title="Autorizados de mi unidad"
                subtitle="Personas que pueden reservar y reportar desde tu unidad."
            />

            {/* Link de activación */}
            <Card className="mb-4 border-accent/30 bg-accent/5">
                <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <LinkIcon className="h-3.5 w-3.5 text-accent" />
                        <p className="text-xs font-semibold text-accent uppercase tracking-widest">Compartir acceso con tus autorizados</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-2">
                        Copia un mensaje listo con el link y las instrucciones para instalar la app.
                        Compartilo por WhatsApp solo con personas que ya cargaste abajo.
                    </p>
                    <div className="rounded-md bg-background border border-border p-2 mb-2 max-h-32 overflow-auto">
                        <pre className="text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap font-sans">{ACTIVATION_MESSAGE}</pre>
                    </div>
                    <Button variant="outline" size="sm" onClick={copiarLink} data-testid="copy-link" className="w-full sm:w-auto">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copiar mensaje completo
                    </Button>
                </CardContent>
            </Card>

            <div className="flex justify-end mb-3">
                <Button onClick={() => setOpen(true)} data-testid="auto-new">
                    <Plus className="h-4 w-4 mr-1" /> Agregar autorizado
                </Button>
            </div>

            {items.length === 0 ? (
                <EmptyState title="Sin autorizados" description="Cargá colegas o asistentes que puedan operar en tu unidad." />
            ) : (
                <div className="grid gap-2">
                    {items.map((u) => {
                        const pendiente = u.pendiente_activacion;
                        return (
                            <Card key={u.usuario_edif_id} data-testid={`auto-${u.usuario_edif_id}`}>
                                <CardContent className="p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-heading font-semibold text-sm truncate">{u.nombre}</p>
                                            <p className="text-xs text-muted-foreground font-mono truncate">{u.email}</p>
                                            {u.whatsapp && (
                                                <p className="text-[11px] text-muted-foreground truncate">
                                                    <span className="text-muted-foreground/70">WhatsApp:</span> <span className="font-mono">{u.whatsapp}</span>
                                                </p>
                                            )}
                                        </div>
                                        <Badge className={pendiente
                                            ? "bg-warning/15 text-warning border border-warning/60 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 hover:bg-warning/15"
                                            : "bg-success/15 text-success border border-success/50 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 hover:bg-success/15"
                                        }>
                                            {pendiente ? "Pendiente" : "Activo"}
                                        </Badge>
                                    </div>
                                    <div className="mt-1 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setDelId(u.usuario_edif_id)}
                                            data-testid={`del-auto-${u.usuario_edif_id}`}
                                            className="text-muted-foreground hover:text-destructive p-1 -mr-1"
                                            aria-label="Eliminar"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Agregar autorizado</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs">Nombre</Label>
                                <Input data-testid="auto-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs">Apellido</Label>
                                <Input data-testid="auto-apellido" value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs">Email</Label>
                            <Input data-testid="auto-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs">WhatsApp</Label>
                            <Input
                                data-testid="auto-whatsapp"
                                value={form.whatsapp}
                                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                                placeholder="Ej: 261 555 1234"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            La persona va a recibir acceso entrando al link de activación y creando su contraseña.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} data-testid="auto-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Querés eliminar este autorizado?</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive hover:bg-destructive/90">
                            {busy ? "Eliminando…" : "Eliminar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
